import { AppApplicationsSlideover } from '#components'
import { z } from '#netzo/shared/utils/zod'
import type { DropdownItem } from '#ui/types'
import type { ComponentProps } from 'vue-component-type-helpers'

// types:

type AppApplicationsSlideoverProps = ComponentProps<typeof AppApplicationsSlideover>

export type ApplicationsUtils = ReturnType<typeof useApplicationsUtils>

export const useApplications = () => {
  const {
    onSubmitCreate,
    onSubmitUpdate,
    onSubmitDelete,
    onSubmitUpdateMultiple,
    slideover,
    modal,
    slideoverOpenInformation,
    slideoverOpenFilePreview,
    slideoverOpenFilePreviewPdf,
    modalOpenDelete,
    modalOpenShareFile,
  } = useRows<Application>('applications')

  const slideoverOpenCreate = (props: AppApplicationsSlideoverProps) => {
    return new Promise<Application>((resolve, reject) => {
      const data = getDefaultApplication(props.data)
      slideover.open(AppApplicationsSlideover, {
        title: 'Crear aplicación',
        ...props,
        data,
        onSubmit: props?.onSubmit ?? (async (event) => {
          try {
            const result = await onSubmitCreate(event.data)
            resolve(result)
          }
          catch (error) {
            reject(error)
          }
          finally {
            slideover.close() // ensures slideover is always closed
          }
        }),
        action: 'create',
        openNestedCallback: slideoverOpenCreate,
      })
    })
  }

  const slideoverOpenEdit = (props: AppApplicationsSlideoverProps) => {
    return new Promise<Application>((resolve, reject) => {
      const data = getDefaultApplication(props.data)
      slideover.open(AppApplicationsSlideover, {
        title: 'Editar aplicación',
        ...props,
        data,
        onSubmit: props?.onSubmit ?? (async (event) => {
          try {
            const result = await onSubmitUpdate(event.data)
            resolve(result)
          }
          catch (error) {
            reject(error)
          }
          finally {
            slideover.close() // ensures slideover is always closed
          }
        }),
        action: 'edit',
        openNestedCallback: slideoverOpenEdit,
      })
    })
  }

  const schema = z.object({
    name: z.string(),
  })

  // actions:

  const getDropdownItems = (row: Application, refresh: () => Promise<void>, utils: ApplicationsUtils, dropdownItemsAction: DropdownItem[] = []): DropdownItem[][] => [
    [
      getDropdownItem('editOrView', 'applications', row, refresh, utils),
    ],
    [
      getDropdownItem('copyId', 'applications', row, refresh, utils),
    ],
    [
      getDropdownItem('notes', 'applications', row, refresh, utils),
      getDropdownItem('files', 'applications', row, refresh, utils),
    ],
    [
      ...dropdownItemsAction,
    ],
    [
      getDropdownItem('info', 'applications', row, refresh, utils),
      getDropdownItem('immutable', 'applications', row, refresh, utils),
    ],
    [
      getDropdownItem('archive', 'applications', row, refresh, utils),
      getDropdownItem('delete', 'applications', row, refresh, utils),
    ],
  ].filter(items => items?.length)

  return {
    onSubmitCreate,
    onSubmitUpdate,
    onSubmitDelete,
    onSubmitUpdateMultiple,
    slideover,
    modal,
    slideoverOpenInformation,
    slideoverOpenFilePreview,
    slideoverOpenFilePreviewPdf,
    modalOpenDelete,
    modalOpenShareFile,
    slideoverOpenCreate,
    slideoverOpenEdit,
    schema,
    getDropdownItems,
  }
}

export const useApplicationsUtils = ({ $industries }: {
  $industries?: Awaited<ReturnType<typeof useFetch<Industry[]>>>
} = {}) => {
  const options = {
    ...optionsApplications,
    industryId: computed(() => $industries?.data.value.map(toOption)),
  }

  const maps = {
    industryId: computed(() => toMapByKey($industries?.data.value, 'id')),
  }

  return { options, maps }
}
